import { forwardRef, memo, useState, useRef, useEffect } from 'react';

import { cn } from '../../lib/styles';

export interface CodeInputProps {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  error?: React.ReactNode;
  containerClassName?: string;
  inputClassName?: string;
  className?: string;
  disabled?: boolean;
}

const CodeInputBase = (
  {
    className,
    length = 6,
    value = '',
    onChange,
    onComplete,
    error,
    containerClassName,
    inputClassName,
    disabled = false,
    ...rest
  }: CodeInputProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const [localValue, setLocalValue] = useState(value);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleInputChange = (index: number, inputValue: string) => {
    if (disabled) return;

    const newValue = localValue.split('');
    newValue[index] = inputValue;
    const result = newValue.join('');

    setLocalValue(result);
    onChange?.(result);

    if (result.length === length && onComplete) {
      onComplete(result);
      return;
    }

    if (inputValue && !localValue[index] && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === 'Backspace' && !localValue[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (disabled) return;

    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, length);
    if (pastedData.length > 0) {
      const result = pastedData.padEnd(length, '');
      setLocalValue(result);
      onChange?.(result);

      if (pastedData.length === length) {
        onComplete?.(pastedData);
        return;
      }

      const nextIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <div className={cn('flex flex-col', className)}>
      <div ref={ref} className={cn('flex gap-2 justify-center', containerClassName)} onPaste={handlePaste} {...rest}>
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={localValue[index] || ''}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className={cn(
              'font-heading-2xl font-semibold text-text-primary text-center',
              'p-3 h-12 w-12',
              'outline-1 outline-bg-inter-secondary-default focus-visible:outline-border-input-select transition-all bg-bg-inter-secondary-default rounded-sm',
              disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
              inputClassName,
            )}
            autoComplete="one-time-code"
            disabled={disabled}
          />
        ))}
      </div>

      {error && <div className="h-5 pt-1 text-text-destructive font-body-sm text-center">{error}</div>}
    </div>
  );
};

export const CodeInput = memo(forwardRef(CodeInputBase));
